import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@mantine/core';
import { IoSearchOutline } from 'react-icons/io5';
import {
  ADMIN_ORDERS_PAGE_SIZE,
  useAdminOrdersPage,
  type Order,
  OrderStatus,
  formatTimeSlot,
  isSlotStartWithinTwoHours,
} from '@modules/orders';
import { OrderStatus as OrderStatusTag } from '@modules/orders/components';
import { DeliveryStatusTag } from '@modules/delivery';
import { Breadcrumbs, OptionsTable, type OptionsTableColumn } from '@components';
import { Input, RefetchButton, Pagination } from '@shared/ui';
import { useDebounce } from '@shared/hooks';
import { formatDate, formatPhoneNumber, formatPrice } from '@shared/utils';
import '@shared/styles/page.css';
import OrdersFiltersPopover, {
  readStoredOrdersFilters,
  writeStoredOrdersFilters,
  type OrdersPageFilters,
} from './OrdersFiltersPopover';
import './OrdersPage.css';

function renderOrderDelivery(order: Order) {
  if (order.deliveryMethod !== 'DELIVERY') return 'Самовывоз';
  if (order.pickupPointData?.deliveryProvider === 'SELLER') return 'Своими силами';
  return (
    <span className="orders-page__delivery-cell">
      <DeliveryStatusTag status={order.dostavistaDeliveryStatus} />
      {order.dostavistaReturnInProgress ? (
        <span className="orders-page__return-tag">Идёт возврат</span>
      ) : null}
    </span>
  );
}

function getSellerAcceptanceText(order: Order): string | null {
  if (order.status !== OrderStatus.CONFIRMED || !order.sellerAcceptanceDeadline) {
    return null;
  }
  return `до ${formatDate(order.sellerAcceptanceDeadline, order.pickupPointData?.city)}`;
}

function selectedFiltersCount(filters: OrdersPageFilters): number {
  return (
    filters.statuses.length +
    filters.cities.length +
    filters.sellerIds.length +
    (filters.dateFrom || filters.dateTo ? 1 : 0) +
    filters.delivery.length
  );
}

function hasOrdersFilters(filters: OrdersPageFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    filters.cities.length > 0 ||
    filters.sellerIds.length > 0 ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.delivery.length > 0
  );
}

export default function OrdersPage() {
  const [filters, setFilters] = useState<OrdersPageFilters>(readStoredOrdersFilters);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebounce(searchQuery.trim(), 300);
  useEffect(() => {
    writeStoredOrdersFilters(filters);
  }, [filters]);
  const filterKey = `${filters.statuses.join(',')}:${filters.cities.join(',')}:${filters.sellerIds.join(',')}:${filters.dateFrom}:${filters.dateTo}:${filters.delivery.join(',')}:${debouncedSearch}`;
  const [pageFilterKey, setPageFilterKey] = useState(filterKey);
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey);
    setPage(1);
  }
  const currentPage = pageFilterKey === filterKey ? page : 1;

  const columns: OptionsTableColumn<Order>[] = [
    {
      key: 'orderNumber',
      label: 'Номер заказа',
      render: (order) => (
        <div>
          <div className="orders-page__order-number">{order.orderNumber}</div>
          {order.dostavistaCreationError ? (
            <div className="orders-page__delivery-error">Не удалось создать доставку</div>
          ) : null}
        </div>
      ),
    },
    ...(filters.statuses.includes(OrderStatus.CONFIRMED)
      ? [
          {
            key: 'sellerAcceptanceDeadline',
            label: 'Принять заказ',
            render: (order: Order) => {
              const text = getSellerAcceptanceText(order);
              return text ? <span className="orders-page__acceptance-deadline">{text}</span> : '—';
            },
          },
        ]
      : []),
    {
      key: 'shopName',
      label: 'Магазин',
      render: (order) => (
        <div>
          <div>{order.shopName || '—'}</div>
          {order.pickupPointData?.city ? (
            <div className="orders-page__shop-city">{order.pickupPointData.city}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'clientPhone',
      label: 'Клиент',
      render: (order) => formatPhoneNumber(order.user?.phone) || '—',
    },
    {
      key: 'timeSlot',
      label: 'Слот времени',
      render: (order) => {
        const text = formatTimeSlot(order.assemblySlotData);
        const isUrgent = isSlotStartWithinTwoHours(
          order.assemblySlotData,
          order.pickupPointData?.timezone,
        );
        return isUrgent ? <span className="orders-page__slot--urgent">{text}</span> : text;
      },
    },
    {
      key: 'totalAmount',
      label: 'Сумма',
      render: (order) => (
        <span className="orders-page__amount">
          {formatPrice(order.totalAmount - (order.deliveryAmount ?? 0))}
        </span>
      ),
    },
    {
      key: 'delivery',
      label: 'Доставка',
      render: (order) => renderOrderDelivery(order),
    },
    {
      key: 'status',
      label: 'Статус',
      render: (order) => <OrderStatusTag status={order.status} />,
    },
  ];

  const { data, isLoading, isFetching, error, refetch } = useAdminOrdersPage(
    {
      statuses: filters.statuses,
      cities: filters.cities,
      sellerIds: filters.sellerIds,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
      delivery: filters.delivery.length > 0 ? filters.delivery : undefined,
    },
    debouncedSearch,
    currentPage,
  );
  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  const listLoading = isLoading || isFetching;
  const navigate = useNavigate();

  const emptyMessage =
    hasOrdersFilters(filters) || debouncedSearch
      ? 'Нет заказов по выбранным фильтрам'
      : 'Нет заказов';
  const selectedCount = selectedFiltersCount(filters);

  const openOrder = (order: Order) => {
    navigate(`/orders/${order.id}`);
  };

  return (
    <div className="page-container orders-page">
      <Breadcrumbs
        items={[
          { label: 'Главная', path: '/' },
          { label: 'Заказы' },
        ]}
      />

      <div className="orders-page__filters">
        <div className="orders-page__search">
          <Input
            className="orders-page__search-input"
            type="text"
            placeholder="Поиск по номеру заказа или клиенту..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftAddon={<IoSearchOutline size={16} aria-hidden />}
          />
        </div>
        <div className="orders-page__select-wrap">
          <OrdersFiltersPopover
            value={filters}
            selectedCount={selectedCount}
            onChange={setFilters}
          />
        </div>
      </div>

      <div className="content-wrapper">
        <div className="orders-table-scroll">
          <div className="orders-table-scroll__body">
            <div className="orders-page__desktop-table">
              <OptionsTable
                columns={columns}
                data={orders}
                loading={listLoading}
                error={error}
                refetch={refetch}
                emptyMessage={emptyMessage}
                onRowClick={openOrder}
              />
            </div>

            <div className="orders-page__mobile-list" aria-label="Список заказов">
              {error && refetch ? (
                <RefetchButton
                  refetch={refetch}
                  isLoading={listLoading}
                  errorMessage={error instanceof Error ? error.message : 'Не удалось загрузить данные'}
                />
              ) : listLoading ? (
                <div className="empty-container">
                  <Loader />
                </div>
              ) : orders.length === 0 ? (
                <div className="orders-page__empty" role="status">
                  {emptyMessage}
                </div>
              ) : (
                orders.map((order) => {
                  const slotText = formatTimeSlot(order.assemblySlotData);
                  const isUrgent = isSlotStartWithinTwoHours(
                    order.assemblySlotData,
                    order.pickupPointData?.timezone,
                  );
                  return (
                    <button
                      key={order.id}
                      type="button"
                      className="orders-page__mobile-card"
                      onClick={() => openOrder(order)}
                    >
                      <div className="orders-page__mobile-card-top">
                        <div className="orders-page__mobile-card-id">
                          <span className="orders-page__order-number">{order.orderNumber}</span>
                          {order.dostavistaCreationError ? (
                            <span className="orders-page__delivery-error">Не удалось создать доставку</span>
                          ) : null}
                        </div>
                        <span className="orders-page__amount">
                          {formatPrice(order.totalAmount - (order.deliveryAmount ?? 0))}
                        </span>
                      </div>
                      <div className="orders-page__mobile-card-meta">
                        <OrderStatusTag status={order.status} />
                      </div>
                      {getSellerAcceptanceText(order) ? (
                        <div className="orders-page__acceptance-deadline">
                          {getSellerAcceptanceText(order)}
                        </div>
                      ) : null}
                      <div className="orders-page__mobile-card-row">
                        <span className="orders-page__mobile-card-label">Магазин</span>
                        <span className="orders-page__mobile-card-value">
                          {order.shopName || '—'}
                          {order.pickupPointData?.city ? (
                            <span className="orders-page__shop-city">{order.pickupPointData.city}</span>
                          ) : null}
                        </span>
                      </div>
                      <div className="orders-page__mobile-card-row">
                        <span className="orders-page__mobile-card-label">Клиент</span>
                        <span className="orders-page__mobile-card-value">
                          {formatPhoneNumber(order.user?.phone) || '—'}
                        </span>
                      </div>
                      <div className="orders-page__mobile-card-row">
                        <span className="orders-page__mobile-card-label">Слот</span>
                        <span
                          className={`orders-page__mobile-card-value${isUrgent ? ' orders-page__slot--urgent' : ''}`}
                        >
                          {slotText}
                        </span>
                      </div>
                      <div className="orders-page__mobile-card-row">
                        <span className="orders-page__mobile-card-label">Доставка</span>
                        <span className="orders-page__mobile-card-value">
                          {renderOrderDelivery(order)}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
          <Pagination
            page={currentPage}
            onChange={setPage}
            totalItems={total}
            pageSize={ADMIN_ORDERS_PAGE_SIZE}
          />
        </div>
      </div>
    </div>
  );
}
